import { OverlayModule } from '@angular/cdk/overlay';
import { DOCUMENT, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi, withXsrfConfiguration } from '@angular/common/http';
import localeSl from '@angular/common/locales/sl';
import { ApplicationConfig, importProvidersFrom, inject, LOCALE_ID, PLATFORM_ID, provideZoneChangeDetection, RendererFactory2 } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router, withInMemoryScrolling } from '@angular/router';
import { ApmModule } from '@elastic/apm-rum-angular';
import {
    AppBasicsModuleConfigModel,
    AppOrigin,
    AuthState,
    MfAuthGuard,
    provideAppBasics,
    provideMfCommonAuth,
    providePasswordCaptchaClient,
    USER_INITIALIZATION_SERVICE,
} from '@meddev/fe-shared';
import { MfCaptchaService, MfCaptchaStrategyEnum, provideMfCaptcha } from '@meddev/fe-shared/mf-captcha';
import { NgbDateAdapter, NgbDateParserFormatter, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { withNgxsLoggerPlugin } from '@ngxs/logger-plugin';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { provideStore } from '@ngxs/store';
import { Intercom, IntercomConfig, IntercomModule } from 'ng-intercom';
import { provideMatomo } from 'ngx-matomo-client';
import { NgxPendoModule } from 'ngx-pendo';
import { NgxPermissionsModule } from 'ngx-permissions';
import { ToastrModule } from 'ngx-toastr';
import { environment } from '../environments/environment';
import { MedifitErrorDefinition } from './_common/constants/medifit-error-definitions';
import './_common/date/dayjs';
import { CustomAdapter, CustomDateParserFormatter } from './_common/datepicker-adapter';
import { EnvironmentHelper } from './_common/helpers/environment-helper';
import { ApiUrlService } from './_common/services/api-url.service';
import { BaseState } from './_common/state/base/base.state';
import { MenuState } from './_common/state/menu/menu.state';
import { UserInitService } from './_modules/public/_services/user-initialization.service';
import { routes } from './app.routes';

EnvironmentHelper.initEnvironment();
registerLocaleData(localeSl);

/** Tracker URL/site for @meddev/fe-shared Matomo initializer (script loads after deferred ngx-matomo setup). */
const APP_MATOMO_TRACKER_CONFIG = {
    siteId: 2,
    trackerUrl: 'https://data.medifit.si/',
} as const;

const APP_INTERCOM_CONFIG: IntercomConfig = {
    appId: environment.INTERCOM.KEY,
    updateOnRouterChange: true,
};

export const appConfig: ApplicationConfig = {
    providers: [
        importProvidersFrom(
            ApmModule,
            NgbModule,
            OverlayModule,
            ToastrModule.forRoot({
                positionClass: 'toast-top-center',
            }),
            IntercomModule.forRoot(APP_INTERCOM_CONFIG),
            NgxPermissionsModule.forRoot(),
            NgxPendoModule.forRoot({
                pendoApiKey: 'bc2a2925-a426-45ac-4dfb-6972378b56b8',
                // @TODO: Ignored with eslint-interactive on 2023-09-15
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                pendoIdFormatter: (value: any) => value.toString().toLowerCase(),
            }),
        ),
        provideStore(
            [BaseState, MenuState, AuthState],
            { developmentMode: !environment.production },
            withNgxsStoragePlugin({
                keys: '*',
            }),
            withNgxsReduxDevtoolsPlugin({
                disabled: environment.production,
            }),
            withNgxsLoggerPlugin({
                disabled: environment.production,
            }),
        ),
        provideMatomo({
            mode: 'deferred',
        }),
        provideAppBasics({
            version: environment.VERSION,
            production: environment.production,
            releaseStage: environment.RELEASE_STAGE as AppBasicsModuleConfigModel['releaseStage'],
            bugSnag: {
                apiKey: 'd13767d6b7050198b956417e6dcd6f4a',
            },
            matomo: {
                siteId: APP_MATOMO_TRACKER_CONFIG.siteId,
                trackerUrl: APP_MATOMO_TRACKER_CONFIG.trackerUrl,
                dimensions: {
                    version: 3,
                    businessSubjectId: 2,
                },
            },
            apmServiceName: 'preventivni-app',
            errorDefinitions: { errors: MedifitErrorDefinition.errors },
        }),
        provideMfCommonAuth({
            authV1Url: ApiUrlService.getAuthV1Url(),
            authV2Url: ApiUrlService.getAuthV2Url(),
            authV2TokenPatterns: environment.mfCommonAuth.authV2TokenPatterns.map(pattern => new RegExp(pattern)),
            loginRedirectUrl: environment.mfCommonAuth.loginRedirectUrl,
            realmName: environment.mfCommonAuth.realmName,
            clientId: environment.mfCommonAuth.clientId,
            impersonationTokenUrlParamName: environment.mfCommonAuth.impersonationTokenUrlParamName,
            additionalIgnoredPatterns: [new RegExp('hooks\\.slack\\.com'), new RegExp('content\\.medifit\\.si')],
            appOrigin: AppOrigin.PREVENTIVNI,
        }),
        provideMfCaptcha({
            language: 'sl',
            v2: environment.reCaptcha.v2,
            v3: environment.reCaptcha.v3,
            defaultStrategy: MfCaptchaStrategyEnum.FALLBACK,
        }),
        providePasswordCaptchaClient(MfCaptchaService),
        provideRouter(
            routes,
            withInMemoryScrolling({
                scrollPositionRestoration: 'top',
            }),
        ),
        MfAuthGuard,
        {
            provide: USER_INITIALIZATION_SERVICE,
            useClass: UserInitService,
        },
        { provide: LOCALE_ID, useValue: 'sl' },
        { provide: NgbDateAdapter, useClass: CustomAdapter },
        { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
        { provide: IntercomConfig, useValue: APP_INTERCOM_CONFIG },
        {
            provide: Intercom,
            useFactory: () =>
                new Intercom(
                    inject(IntercomConfig),
                    inject(PLATFORM_ID),
                    inject(Router),
                    inject(RendererFactory2),
                    inject(DOCUMENT, { optional: true }),
                ),
        },
        provideHttpClient(withInterceptorsFromDi(), withXsrfConfiguration({})),
        provideAnimations(),
        provideZoneChangeDetection({
            eventCoalescing: true,
            runCoalescing: true,
        }),
    ],
};
